/* shared/charts — dashboard primitives.
   Extracted from dashboard-premium.jsx; window.* globals replaced with ESM
   imports/exports. Behavior preserved verbatim. */
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { NexusIcon } from '../ui/NexusIcon';

/* ---------- deterministic seeded RNG ---------- */
export const hashKey = (key) => {
  let h = 2166136261;
  const s = String(key || '');
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = (h * 16777619) >>> 0; }
  return h >>> 0;
};

export const seededSeries = (key, n, base = 50, variance = 35) => {
  let h = hashKey(key);
  const out = [];
  for (let i = 0; i < n; i++) {
    h = (h * 1664525 + 1013904223) >>> 0;
    const r = (h % 1000) / 1000;
    const prev = out[i - 1] != null ? out[i - 1] : base;
    const drift = (r - 0.5) * variance;
    out.push(Math.max(2, prev * 0.55 + (base + drift) * 0.45));
  }
  return out;
};

/* ---------- CountUp ---------- */
export const CountUp = ({ to, duration = 800, prefix = '', suffix = '', decimals = 0, format }) => {
  const [v, setV] = useState(0);
  const fromRef = useRef(0);
  const rafRef = useRef(null);
  const target = typeof to === 'number' ? to : parseFloat(String(to).replace(/[^\d.\-]/g, '')) || 0;
  useEffect(() => {
    const from = fromRef.current;
    const start = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      const cur = from + (target - from) * eased;
      setV(cur);
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
      else fromRef.current = target;
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => rafRef.current && cancelAnimationFrame(rafRef.current);
  }, [target, duration]);
  const display = format ? format(v) : (prefix + v.toFixed(decimals) + suffix);
  return <span style={{ fontVariantNumeric: 'tabular-nums' }}>{display}</span>;
};

/* ---------- Sparkline ---------- */
export const Sparkline = ({ points, color = '#125AB0', height = 36, fill = true, showDot = true, format }) => {
  const wrapRef = useRef(null);
  const [hover, setHover] = useState(null);
  const w = 100, h = 36;
  const max = Math.max(...points, 1);
  const min = Math.min(...points, 0);
  const range = max - min || 1;
  const xy = (v, i) => [(i / (points.length - 1)) * w, h - ((v - min) / range) * (h - 4) - 2];
  const path = points.map((v, i) => { const [x, y] = xy(v, i); return (i === 0 ? 'M' : 'L') + x.toFixed(1) + ' ' + y.toFixed(1); }).join(' ');
  const gid = useMemo(() => 'sp-' + Math.random().toString(36).slice(2, 7), []);
  const last = points[points.length - 1];
  const onMove = (e) => {
    if (!wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * w;
    const i = Math.max(0, Math.min(points.length - 1, Math.round((x / w) * (points.length - 1))));
    setHover({ i, v: points[i] });
  };
  return (
    <div className="nx-spark2" ref={wrapRef} onMouseMove={onMove} onMouseLeave={() => setHover(null)} style={{ height }}>
      <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" width="100%" height="100%">
        <defs>
          <linearGradient id={gid} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.28" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        {fill && <path d={path + ` L ${w} ${h} L 0 ${h} Z`} fill={`url(#${gid})`} />}
        <path d={path} stroke={color} strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
        {showDot && (() => { const [x, y] = xy(last, points.length - 1); return <circle cx={x} cy={y} r="1.6" fill={color} />; })()}
        {hover && (() => {
          const [x, y] = xy(hover.v, hover.i);
          return (
            <g>
              <line x1={x} x2={x} y1="0" y2={h} stroke={color} strokeOpacity="0.25" strokeDasharray="2 2" vectorEffect="non-scaling-stroke" />
              <circle cx={x} cy={y} r="2" fill="#fff" stroke={color} strokeWidth="1.4" vectorEffect="non-scaling-stroke" />
            </g>
          );
        })()}
      </svg>
      {hover && (
        <span className="nx-spark-tip" style={{ left: `${(hover.i / (points.length - 1)) * 100}%` }}>
          {format ? format(hover.v) : hover.v.toFixed(0)}
        </span>
      )}
    </div>
  );
};

/* ---------- RingMeter ---------- */
export const RingMeter = ({ value, size = 30, stroke = 3.5, color = '#125AB0', track = 'rgba(15,42,74,0.10)', label }) => {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const off = c * (1 - Math.max(0, Math.min(100, value)) / 100);
  return (
    <div className="nx-ring" style={{ width: size, height: size }}>
      <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={track} strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
          strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dashoffset .5s ease' }} />
      </svg>
      <span className="nx-ring-text">{label != null ? label : Math.round(value) + '%'}</span>
    </div>
  );
};

/* ---------- OwnerAvatar ---------- */
export const OwnerAvatar = ({ name, size = 24, title }) => {
  const n = name || '?';
  const h = hashKey(n);
  const palette = [
    ['#FFE2D6', '#B5481B'], ['#DCEAFB', '#125AB0'], ['#DDF3E5', '#0E8C5A'],
    ['#F5E2FB', '#6E3FBA'], ['#FFF1CC', '#8A6300'], ['#FFD9DC', '#A02C2C'],
    ['#DBF1F4', '#0E6F86'], ['#E6E1F8', '#3E2FA0'],
  ];
  const [bg, fg] = palette[h % palette.length];
  const initials = n.split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0]).join('').toUpperCase();
  return (
    <span className="nx-own-avatar" title={title || n} style={{ width: size, height: size, background: bg, color: fg, fontSize: Math.round(size * 0.42) }}>
      {initials || '?'}
    </span>
  );
};

/* ---------- Collapse / layout persistence ---------- */
const LAYOUT_KEY = 'nexus-dash-layout-v1';
const loadLayout = () => { try { return JSON.parse(localStorage.getItem(LAYOUT_KEY) || '{}'); } catch (e) { return {}; } };
const saveLayout = (l) => { try { localStorage.setItem(LAYOUT_KEY, JSON.stringify(l)); } catch (e) {} };

export const useDashboardLayout = () => {
  const [layout, setLayout] = useState(loadLayout);
  const collapsed = (id) => !!layout.collapsed?.[id];
  const toggle = (id) => setLayout(prev => {
    const next = { ...prev, collapsed: { ...(prev.collapsed || {}), [id]: !prev.collapsed?.[id] } };
    saveLayout(next); return next;
  });
  const reset = () => { saveLayout({}); setLayout({}); };
  return { layout, collapsed, toggle, reset };
};

/* ---------- PremiumPanel ---------- */
export const PremiumPanel = ({ id, title, icon, badge, tone, actions, className = '', children, collapsible = true, layout }) => {
  const isCollapsed = layout ? layout.collapsed(id) : false;
  return (
    <div className={'nx-panel nx-panel-p ' + (isCollapsed ? 'is-collapsed ' : '') + className}>
      <div className="nx-panel-head">
        <h3 className="nx-panel-title">
          {icon && <span className={'nx-panel-icon ' + (tone ? 'tone-' + tone : '')}><NexusIcon name={icon} size={12} /></span>}
          {title}
        </h3>
        {badge && <span className="nx-pill nx-pill-soft">{badge}</span>}
        <div className="nx-panel-actions">
          {actions}
          {collapsible && layout && (
            <button className="nx-icon-btn" onClick={() => layout.toggle(id)} title={isCollapsed ? 'Expand' : 'Collapse'}>
              <NexusIcon name={isCollapsed ? 'ChevronDown' : 'ChevronUp'} size={12} />
            </button>
          )}
        </div>
      </div>
      {!isCollapsed && <div className="nx-panel-body">{children}</div>}
    </div>
  );
};
